import { useEffect, useState } from "react";
import { MessageCircle, Send, RefreshCw, CheckCircle2, AlertCircle, Phone, History } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function WhatsApp() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testCity, setTestCity] = useState("Roma");
  const [testText, setTestText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/v3/whatsapp/status"),
        api.get("/v3/whatsapp/log?limit=50"),
      ]);
      setStatus(s.data || null);
      setLog(l.data?.items || []);
    } catch (e) {
      toast.error("Errore caricamento WhatsApp");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Inserisci un numero destinatario"); return; }
    setBusy(true);
    try {
      const r = await api.post("/v3/whatsapp/test-send", {
        to_phone: testTo.trim(),
        city: testCity || null,
        text: testText || null,
      });
      if (r.data?.ok) toast.success(`Inviato! message_id: ${r.data.result?.message_id || "—"}`);
      else toast.error(`Errore: ${JSON.stringify(r.data?.result?.detail || r.data?.result?.error || "unknown").substring(0, 200)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const isOk = status?.configured && status?.reachable;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="whatsapp-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
            <MessageCircle className="h-9 w-9 text-emerald-400" />
            WhatsApp <span className="text-emerald-400">Cloud API</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">Mittente: <code className="text-emerald-300">{status?.sender_number || "+393392662954"}</code> · Cart Recovery automatico ogni 60s</p>
        </div>
        <button onClick={load} disabled={loading} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 flex items-center gap-2 text-sm" data-testid="wa-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>

      {/* STATUS BANNER */}
      {loading ? (
        <div className="text-center text-slate-500 py-8">Caricamento…</div>
      ) : isOk ? (
        <div className="p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/40" data-testid="wa-status-ok">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="text-emerald-400 h-6 w-6" />
            <div className="font-semibold text-emerald-300">WhatsApp Cloud API connesso e raggiungibile</div>
          </div>
          <div className="text-xs text-slate-300 space-y-1">
            <div>Numero attivo: <code className="text-emerald-200">{status.meta?.display_phone_number || status.sender_number}</code></div>
            <div>Verified Name: <code className="text-emerald-200">{status.meta?.verified_name || "—"}</code></div>
            <div>Quality Rating: <code className="text-emerald-200">{status.meta?.quality_rating || "—"}</code></div>
            <div>Template: <code className="text-emerald-200">{status.template_name}</code> ({status.template_lang})</div>
            <div>Phone Number ID: <code className="text-emerald-200">{status.phone_number_id}</code></div>
          </div>
        </div>
      ) : (
        <div className="p-5 rounded-xl bg-amber-500/10 border border-amber-500/40" data-testid="wa-status-missing">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="text-amber-400 h-6 w-6" />
            <div className="font-semibold text-amber-300">
              {status?.configured === false ? "WhatsApp Cloud API non configurato" : "Configurazione presente ma irraggiungibile"}
            </div>
          </div>
          <div className="text-xs text-slate-300 space-y-2">
            {status?.missing?.length > 0 && (
              <div>
                <b className="text-amber-300">Mancano in <code>backend/.env</code>:</b>
                <ul className="list-disc pl-5 mt-1">
                  {status.missing.map((k) => <li key={k}><code className="text-rose-300">{k}</code></li>)}
                </ul>
              </div>
            )}
            {status?.error && (
              <div className="bg-rose-500/10 border border-rose-500/40 rounded p-2 text-rose-200 text-[11px] font-mono whitespace-pre-wrap">
                {typeof status.error === "string" ? status.error : JSON.stringify(status.error, null, 2)}
              </div>
            )}
            <div className="bg-slate-900/60 rounded p-3 mt-3 text-slate-300">
              <b className="text-emerald-300">Come ottenere le credenziali:</b>
              <ol className="list-decimal pl-5 mt-1.5 space-y-1">
                <li>developers.facebook.com → My Apps → la tua App WhatsApp Business</li>
                <li>WhatsApp → API Setup → copia il <b>Permanent Access Token</b> in <code>WHATSAPP_TOKEN</code></li>
                <li>Sotto, copia il <b>Phone Number ID</b> del numero +393392662954 in <code>WHATSAPP_PHONE_NUMBER_ID</code></li>
                <li>Copia il <b>Business Account ID (WABA)</b> in <code>WHATSAPP_BUSINESS_ACCOUNT_ID</code></li>
                <li>Crea il template <code>cart_recovery_discount_5</code> con 1 parametro {`{{1}}`} = città, lingua <code>it</code>, tipo Marketing</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* TEST SEND */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Send className="h-5 w-5 text-emerald-400" /> Invio di prova
        </h3>
        <p className="text-xs text-slate-400 mb-4">Manda un messaggio reale (template di default = cart recovery con 5% sconto). Per il test text mode, l&apos;utente deve averti scritto nelle ultime 24h.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Numero destinatario</label>
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="+393331234567"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200 font-mono"
              data-testid="wa-test-phone"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Città (param template)</label>
            <input
              value={testCity}
              onChange={(e) => setTestCity(e.target.value)}
              placeholder="Roma"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200"
              data-testid="wa-test-city"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Testo libero (override template)</label>
            <input
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Ciao, conferma la tua prenotazione…"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200"
              data-testid="wa-test-text"
            />
          </div>
        </div>
        <button
          onClick={sendTest}
          disabled={busy || !testTo.trim()}
          className="mt-4 px-5 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
          data-testid="wa-send-btn"
        >
          <Send className="h-4 w-4" /> {busy ? "Invio…" : "Invia messaggio"}
        </button>
      </div>

      {/* LOG */}
      <div>
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <History className="h-5 w-5 text-emerald-400" /> Cronologia invii
        </h3>
        {log.length === 0 ? (
          <div className="text-center py-10 text-slate-500 border border-dashed border-slate-800 rounded-xl">Nessun invio registrato.</div>
        ) : (
          <div className="space-y-2">
            {log.map((l) => (
              <div key={l.id} className={`p-3 rounded-lg border ${l.ok ? "bg-emerald-500/5 border-emerald-500/30" : "bg-rose-500/5 border-rose-500/30"} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm`} data-testid={`wa-log-${l.id}`}>
                <div className="flex items-center gap-2">
                  {l.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertCircle className="h-4 w-4 text-rose-400" />}
                  <Phone className="h-3.5 w-3.5 text-slate-500" />
                  <span className="font-mono text-slate-200">{l.to}</span>
                  <span className="text-xs text-slate-500">· {l.mode}</span>
                  {l.template && <span className="text-xs text-slate-400">· {l.template}</span>}
                  {l.city && <span className="text-xs text-slate-400">· {l.city}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {l.message_id && <code className="text-[10px] text-emerald-300 truncate max-w-[180px]">{l.message_id}</code>}
                  {l.error && <span className="text-xs text-rose-300 truncate max-w-[260px]">{typeof l.error === "string" ? l.error : JSON.stringify(l.error).substring(0, 100)}</span>}
                  <span className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString("it-IT")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
